'use strict';
import { NestedTreeControl } from '@angular/cdk/tree';
import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { clone } from '@helpers/arrays';
import { LookupNode } from '@models/lookup-node';
import { LookupSetup, TableType } from '@models/lookup-setup';
import { TableDataService } from '@services/lookup-tables/table-data.service';
import { TableSetupService } from '@services/lookup-tables/table-setup.service';
import { MenuService } from '@services/menu/menu.service';
import { BehaviorSubject, of, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { LookupDatabase } from './helpers/lookup-database';

@Component({
  selector: 'app-lookup-tree',
  templateUrl: './lookup-tree.component.html',
  styleUrls: ['./lookup-tree.component.scss']
})
export class LookupTreeComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() scenarioId: number = null;
  private subscriptions: Subscription[] = [];
  public nestedDataSource: MatTreeNestedDataSource<LookupNode>;
  public nestedTreeControl: NestedTreeControl<LookupNode>;
  public selectedItem = null;
  public selectedTreeItem: LookupNode = null;
  public selectedTreeItem$ = new BehaviorSubject<LookupNode>(null);
  public showTree = true;
  public updatedTables = {};
  public descriptionTable = {};

  private setSelectedItem = (node: LookupNode = null) => {
    if (node) {
      this.selectedTreeItem = node;
      this.selectedItem = node.name;
      this.selectedTreeItem$.next(node);
    }
  };

  constructor(
    public database: LookupDatabase,
    public menu: MenuService,
    private tableSetupService: TableSetupService,
    private tableDataService: TableDataService,
    private changeRef: ChangeDetectorRef
  ) {
    this.nestedTreeControl = new NestedTreeControl<LookupNode>(this._getChildren);
    this.nestedDataSource = new MatTreeNestedDataSource();
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.launchListeners();
    this.loadLookupTablesData();
    this.changeRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private launchListeners() {
    this.subscriptions = [this.database.dataChange.subscribe(data => (this.nestedDataSource.data = data))];
  }

  private async loadLookupTablesData() {
    const apiData: LookupSetup[] = (await this.tableSetupService
      .getLookupTablesWithData()
      .pipe(debounceTime(200))
      .toPromise()) as LookupSetup[];
    if (apiData) {
      this.tableDataService.lookupTables = clone(apiData);

      if (this.tableDataService.scenarioSwitchState === 'scenario') {
        this.database.initialize(this.tableDataService.scenarioTables);
      } else {
        this.database.initialize(this.tableDataService.globalTables);
      }

      this.descriptionTable = this.database.descriptionTable;

      if (this.nestedDataSource && this.nestedDataSource.data.length > 0) {
        this.updateTreeOnScenarioGlobalSwitch().then(() => {
          const rootNode = this.nestedDataSource.data.find((node: LookupNode) => node.name === 'ALL');
          this.selectNode(rootNode);
        });
      }
    }
  }

  private async updateTreeOnScenarioGlobalSwitch() {
    const treeFilter$ = this.tableDataService.filterTree$.subscribe((tablesData: any) => {
      if (tablesData) {
        if (tablesData === 'scenario') {
          this.database.initialize(this.tableDataService.scenarioTables);
        } else {
          this.database.initialize(this.tableDataService.globalTables);
        }

        this.descriptionTable = this.database.descriptionTable;
      }
    });
    this.subscriptions.push(treeFilter$);
  }

  public isTableModified = (mnemonic: string) => this.updatedTables[mnemonic];

  // public isItemSelected = (name: string) => name === this.selectedItem;
  public isItemSelected = (node: LookupNode) =>
    node !== null &&
    this.selectedTreeItem !== null &&
    node.name === this.selectedTreeItem.name &&
    node.id === this.selectedTreeItem.id;

  public isExpandedNode = (node: LookupNode = null) => (node ? this.nestedTreeControl.isExpanded(node) : false);

  public hasNestedChild = (_: number, node: LookupNode) => {
    // Exception for categories
    const isCategoryNode = this.database.categoryGroups
      .map(catName => catName.toLowerCase())
      .includes(node.name.toLowerCase());
    return isCategoryNode ? true : !node.value && node.children && node.children.length > 0;
  };

  private _getChildren = (node: LookupNode) => of(node.children);

  /**
   * Selects the node on click
   * @param { LookupNode } node
   * @memberof LookupTreeComponent
   */
  public selectNode(node: LookupNode = null) {
    if (node) {
      this.expandNode(node);
      this.setSelectedItem(node);
    }
  }

  public toggleNode(node: LookupNode = null): void {
    if (node) {
      this.nestedTreeControl.toggle(node);
    }
  }

  public expandNode = (node: LookupNode = null): void => {
    if (node) {
      this.nestedTreeControl.expand(node);
    }
  };

  public collapseNode = (node: LookupNode = null): void => {
    if (node) {
      this.nestedTreeControl.collapse(node);
    }
  };

  public expandDescendants(targetNode: LookupNode = null) {
    if (targetNode) {
      // this.nestedTreeControl.expandDescendants(rootNode);
      targetNode.children.forEach(children => this.nestedTreeControl.expand(children));
      // console.log('Tree expanded recursively');
    }
  }

  public viewAll() {
    if (this.nestedDataSource.data.length > 0) {
      const rootNode = this.nestedDataSource.data.find((node: LookupNode) => node.name === 'ALL');
      this.expandDescendants(rootNode);
      setTimeout(() => this.selectNode(rootNode));
    }
  }

  public switchSelected(itemName: TableType) {
    if (itemName !== this.tableDataService.scenarioSwitchState) {
      if (itemName === 'scenario') {
        this.database.initialize(this.tableDataService.scenarioTables);
      } else {
        this.database.initialize(this.tableDataService.globalTables);
      }
      this.descriptionTable = this.database.descriptionTable;

      this.viewAll();
      this.tableDataService.scenarioSwitchState = itemName;
    }
    // this.viewAll();
  }

  get isEditingAssumptions() {
    return this.menu.isEditingAssumptions;
  }
}
